import {
    type PropsWithChildren as PWC,
    type ReactNode,
    useCallback,
    useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
    AppShell,
    Heading1,
    Image,
    Overlay,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';
import { toKebabCase } from '@phx/utils';

import { useBrandContext } from '../../contexts/BrandProvider';

import { PrescryptivePoweredModal } from './PrescryptivePoweredModal';

export interface PageLayoutProps {
    loadingOverlay?: ReactNode;
    header?: ReactNode;
    heading?: ReactNode;
    title?: string;
    footer?: ReactNode;
    homeUrl?: string;
    hideLogo?: boolean;
}

export const PageLayout = ({
    loadingOverlay,
    header,
    heading,
    title,
    footer,
    children,
    homeUrl = import.meta.env.VITE_MYPHX_UNAUTH_URL,
    hideLogo = false,
}: PWC<PageLayoutProps>) => {
    const { key } = useLocation();
    const { backOverrideState } = useNavBar();
    const { logoPath, brandId } = useBrandContext();
    const [opened, { toggle }] = useDisclosure(false);

    const handleBack = useMemo(() => {
        // Explanation here https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey
        const isInitialLocation = key === 'default';

        if (backOverrideState.overrideFn) return backOverrideState.overrideFn;
        if (isInitialLocation) return;

        return;
    }, [key, backOverrideState.overrideFn]);

    const goHome = useCallback(() => {
        if (brandId === 'Prescryptive') {
            window.location.replace(homeUrl);
            return;
        }

        toggle();
    }, [homeUrl, brandId, toggle]);

    const appHeader =
        brandId === 'Prescryptive' ? (
            header
        ) : (
            <>
                <Stack justify="center" align="center" gap="sm">
                    <Image
                        src={`/static/img/${toKebabCase(brandId)}-logo.svg`}
                        w="144"
                        h="48"
                        fit="contain"
                    />
                </Stack>
                {header}
            </>
        );

    return (
        <>
            <AppShell
                header={appHeader}
                navBarProps={{
                    logoUrl: hideLogo ? undefined : logoPath,
                    // Always center logo
                    logoStyles: {
                        position: 'absolute',
                        left: '0',
                        right: '0',
                        marginInline: 'auto',
                    },
                    onBackButtonClick: handleBack,
                    onLogoClick: goHome,
                }}
            >
                {loadingOverlay ? <Overlay visible loader fullScreen /> : null}
                <Stack gap="lg" flex="1">
                    <Stack gap="lg" flex={{ base: '1', sm: 'unset' }}>
                        {heading ? heading : <Heading1>{title}</Heading1>}
                        {children}
                    </Stack>
                    {footer}
                </Stack>
            </AppShell>
            <PrescryptivePoweredModal opened={opened} toggle={toggle} />
        </>
    );
};

export default PageLayout;
